// UTM Parameter Tracking for Ad Campaign Attribution
// Tracks: Facebook, Instagram, TikTok, Snapchat, and other platforms

export interface UTMParams {
  utm_source?: string;      // e.g., facebook, instagram, tiktok, snapchat
  utm_medium?: string;      // e.g., paid, cpc, social
  utm_campaign?: string;    // Your campaign name
  utm_term?: string;        // Keyword (if applicable)
  utm_content?: string;     // Ad creative identifier
  landing_page?: string;    // First page they landed on
  referrer?: string;        // Document referrer
  timestamp?: string;       // When they first arrived
}

const UTM_STORAGE_KEY = 'proton_utm_params';
const UTM_SESSION_KEY = 'proton_utm_session';

// Platform display names for reporting
export const PLATFORM_NAMES: Record<string, string> = {
  facebook: 'Facebook',
  fb: 'Facebook',
  instagram: 'Instagram',
  ig: 'Instagram',
  tiktok: 'TikTok',
  tt: 'TikTok',
  snapchat: 'Snapchat',
  snap: 'Snapchat',
  google: 'Google Ads',
  bing: 'Bing Ads',
  twitter: 'Twitter/X',
  linkedin: 'LinkedIn',
  email: 'Email',
  direct: 'Direct',
  organic: 'Organic',
};

/**
 * Capture UTM parameters from URL on page load
 * Should be called once when the app initializes
 */
export function captureUTMParams(): UTMParams | null {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  
  // Check if there are any UTM params in the URL
  const hasUTMParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
    .some(param => urlParams.has(param));

  if (hasUTMParams) {
    const utmParams: UTMParams = {
      utm_source: urlParams.get('utm_source') || undefined,
      utm_medium: urlParams.get('utm_medium') || undefined,
      utm_campaign: urlParams.get('utm_campaign') || undefined,
      utm_term: urlParams.get('utm_term') || undefined,
      utm_content: urlParams.get('utm_content') || undefined,
      landing_page: window.location.pathname,
      referrer: document.referrer || undefined,
      timestamp: new Date().toISOString(),
    };

    // Store in localStorage (persists across sessions)
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
    
    // Also store in sessionStorage (for current session tracking)
    sessionStorage.setItem(UTM_SESSION_KEY, JSON.stringify(utmParams));

    if (import.meta.env.DEV) console.log('[UTM] Captured params:', utmParams);
    return utmParams;
  }

  return null;
}

/**
 * Get stored UTM parameters
 * Prioritizes session storage, falls back to localStorage
 */
export function getUTMParams(): UTMParams | null {
  if (typeof window === 'undefined') return null;

  // First check session storage (current visit)
  const sessionParams = sessionStorage.getItem(UTM_SESSION_KEY);
  if (sessionParams) {
    return JSON.parse(sessionParams);
  }

  // Fall back to localStorage (returning visitor)
  const storedParams = localStorage.getItem(UTM_STORAGE_KEY);
  if (storedParams) {
    return JSON.parse(storedParams);
  }

  return null;
}

/**
 * Get the source platform name (normalized)
 */
export function getSourcePlatform(): string {
  const params = getUTMParams();
  if (!params?.utm_source) return 'Direct';
  
  const source = params.utm_source.toLowerCase();
  return PLATFORM_NAMES[source] || params.utm_source;
}

/**
 * Clear UTM parameters (e.g., after conversion)
 */
export function clearUTMParams(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(UTM_STORAGE_KEY);
  sessionStorage.removeItem(UTM_SESSION_KEY);
}

/**
 * Get attribution summary for analytics/conversion tracking
 */
export function getAttributionData(): Record<string, string | undefined> {
  const params = getUTMParams();
  if (!params) {
    return {
      source: 'direct',
      platform: 'Direct',
    };
  }

  return {
    source: params.utm_source,
    medium: params.utm_medium,
    campaign: params.utm_campaign,
    term: params.utm_term,
    content: params.utm_content,
    platform: getSourcePlatform(),
    landing_page: params.landing_page,
    first_visit: params.timestamp,
  };
}

/**
 * Check if visitor came from a specific platform
 */
export function isFromPlatform(platform: string): boolean {
  const params = getUTMParams();
  if (!params?.utm_source) return false;
  
  const source = params.utm_source.toLowerCase();
  const platformLower = platform.toLowerCase();
  
  // Check both the exact match and common abbreviations
  return source === platformLower || 
         PLATFORM_NAMES[source]?.toLowerCase() === platformLower;
}

// Initialize on module load
if (typeof window !== 'undefined') {
  // Capture UTM params immediately when the script loads
  captureUTMParams();
}
